import { useState, useEffect } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';

function getStatusMeta(t) {
  return {
    trial:     { label: t('statusTrial'),        color: 'var(--yellow)', icon: '🧪' },
    active:    { label: t('statusActiveSub'),    color: 'var(--green)',  icon: '✅' },
    expired:   { label: t('statusExpired'),      color: 'var(--red)',    icon: '⛔' },
    cancelled: { label: t('statusCancelledSub'), color: 'var(--text3)', icon: '🚫' },
  };
}

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('uz-UZ', { day: '2-digit', month: 'long', year: 'numeric' });
}

export default function Obuna() {
  const { t } = useLang();
  const [sub, setSub]       = useState(null);
  const [plans, setPlans]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(null);
  const [msg, setMsg]       = useState(null);

  const STATUS_META = getStatusMeta(t);

  useEffect(() => {
    Promise.all([
      api.get('/subscriptions/my').then(r => r.data),
      api.get('/subscriptions/plans').then(r => r.data),
    ]).then(([s, p]) => {
      setSub(s);
      setPlans(p);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  async function activate(planId) {
    setActivating(planId);
    setMsg(null);
    try {
      await api.post('/subscriptions/test-activate', { plan_id: planId, months: 1 });
      const updated = await api.get('/subscriptions/my').then(r => r.data);
      setSub(updated);
      setMsg({ type: 'success', text: t('activatedSuccess') });
    } catch (e) {
      setMsg({ type: 'error', text: '❌ ' + (e.response?.data?.error || t('error')) });
    } finally {
      setActivating(null);
    }
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ fontFamily: 'Orbitron, sans-serif', color: 'var(--cyan)', fontSize: 18 }}>{t('loading')}</div>
    </div>
  );

  const statusMeta = STATUS_META[sub?.status] || STATUS_META.trial;

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{t('obunaTitleFull')}</h1>
        <div style={{ color: 'var(--text2)', fontSize: 13 }}>{t('obunaSubtitle')}</div>
      </div>

      {msg && (
        <div style={{
          padding: '12px 18px', borderRadius: 10, marginBottom: 20,
          background: msg.type === 'success' ? 'rgba(0,255,136,0.1)' : 'rgba(255,59,92,0.1)',
          border: `1px solid ${msg.type === 'success' ? 'var(--green)' : 'var(--red)'}`,
          color: msg.type === 'success' ? 'var(--green)' : 'var(--red)', fontSize: 13,
        }}>{msg.text}</div>
      )}

      <div style={{
        background: 'var(--card2)', border: `1px solid ${statusMeta.color}40`,
        borderRadius: 16, padding: '24px 28px', marginBottom: 28,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%', fontSize: 28,
            background: statusMeta.color + '18', border: `2px solid ${statusMeta.color}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{statusMeta.icon}</div>
          <div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 4 }}>{t('currentStatus')}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: statusMeta.color }}>{statusMeta.label}</div>
            {sub?.plan_name && (
              <div style={{ fontSize: 13, color: 'var(--cyan)', marginTop: 3 }}>
                {sub.plan_name} {t('planSuffix')}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, textAlign: 'center' }}>
          <div>
            <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--cyan)' }}>
              {sub?.computer_count || 0}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>{t('computersLabel')}</div>
          </div>
          <div>
            <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 22, fontWeight: 700, color: sub?.days_left < 3 ? 'var(--red)' : 'var(--green)' }}>
              {sub?.days_left ?? '—'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>{t('daysLeft')}</div>
          </div>
          <div>
            <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 20, fontWeight: 700, color: 'var(--purple)' }}>
              {sub?.max_computers ? `${sub.computer_count}/${sub.max_computers}` : '—'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>{t('limitLabel')}</div>
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          {sub?.status === 'trial' && (
            <div style={{ fontSize: 12, color: 'var(--yellow)', marginBottom: 6 }}>
              {t('trialUntil')} {fmtDate(sub.trial_ends_at)} {t('trialUntilSuffix')}
            </div>
          )}
          {sub?.status === 'active' && (
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>
              {t('expiresLabel')} {fmtDate(sub.current_period_end)}
            </div>
          )}
        </div>
      </div>

      <div style={{
        background: 'rgba(255,184,0,0.05)', border: '1px solid rgba(255,184,0,0.3)',
        borderRadius: 12, padding: '12px 18px', marginBottom: 28, fontSize: 12,
        color: 'var(--yellow)', display: 'flex', alignItems: 'center', gap: 10,
      }}>
        🧪 <span>{t('testModeNote')}</span>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: 'var(--text2)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
          {t('subscriptionPlans')}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
        {plans.map(plan => {
          const isCurrentPlan = sub?.plan_id === plan.id && sub?.status === 'active';
          const isRecommended = sub?.recommended_plan?.id === plan.id;
          const features = Array.isArray(plan.features) ? plan.features : JSON.parse(plan.features || '[]');

          return (
            <div key={plan.id} style={{
              background: isCurrentPlan ? 'rgba(0,212,255,0.06)' : isRecommended ? 'rgba(124,58,237,0.06)' : 'var(--card2)',
              border: `1px solid ${isCurrentPlan ? 'var(--cyan)' : isRecommended ? 'var(--purple)' : 'var(--border)'}`,
              borderRadius: 16, padding: '24px 20px', position: 'relative', transition: 'all 0.2s',
              boxShadow: isRecommended ? '0 0 30px rgba(124,58,237,0.15)' : 'none',
            }}>
              {isRecommended && !isCurrentPlan && (
                <div style={{
                  position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)',
                  background: 'var(--purple)', color: 'white', fontSize: 10, fontWeight: 700,
                  padding: '3px 12px', borderRadius: 20, whiteSpace: 'nowrap',
                }}>{t('recommendedBadge')}</div>
              )}
              {isCurrentPlan && (
                <div style={{
                  position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)',
                  background: 'var(--cyan)', color: '#000', fontSize: 10, fontWeight: 700,
                  padding: '3px 12px', borderRadius: 20, whiteSpace: 'nowrap',
                }}>{t('activePlanBadge')}</div>
              )}

              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{plan.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 16 }}>
                {plan.max_computers >= 999 ? t('unlimitedComputers') : `${plan.max_computers} ${t('computersUpToSuffix')}`}
              </div>

              <div style={{ marginBottom: 20 }}>
                <span style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--cyan)' }}>
                  {Number(plan.price_monthly).toLocaleString()}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text2)' }}> {t('perMonth')}</span>
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', fontSize: 11, color: 'var(--text2)' }}>
                {features.map((f, i) => (
                  <li key={i} style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: 'var(--green)', fontSize: 10 }}>✓</span> {f}
                  </li>
                ))}
              </ul>

              {isCurrentPlan ? (
                <div style={{
                  textAlign: 'center', padding: '10px', borderRadius: 10,
                  background: 'rgba(0,212,255,0.1)', color: 'var(--cyan)', fontSize: 12, fontWeight: 600,
                }}>{t('activePlanLabel')}</div>
              ) : (
                <button
                  onClick={() => activate(plan.id)}
                  disabled={activating === plan.id}
                  style={{
                    width: '100%', padding: '10px', borderRadius: 10,
                    background: isRecommended ? 'linear-gradient(135deg, var(--purple), #5b21b6)' : 'rgba(0,212,255,0.08)',
                    border: `1px solid ${isRecommended ? 'transparent' : 'var(--cyan)'}`,
                    color: isRecommended ? 'white' : 'var(--cyan)',
                    cursor: activating ? 'not-allowed' : 'pointer',
                    fontSize: 13, fontWeight: 600, fontFamily: 'Inter, sans-serif',
                    opacity: activating && activating !== plan.id ? 0.5 : 1,
                  }}
                >
                  {activating === plan.id ? t('activatingLabel') : t('testActivate')}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div style={{
        background: 'var(--card2)', border: '1px solid var(--border)',
        borderRadius: 14, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, fontSize: 12,
      }}>
        <span style={{ fontSize: 20 }}>💳</span>
        <div>
          <div style={{ color: 'var(--text2)', marginBottom: 2 }}>{t('realPaymentTitle')}</div>
          <div style={{ color: 'var(--text3)' }}>{t('realPaymentDesc')}</div>
        </div>
        <div style={{ marginLeft: 'auto', color: 'var(--text3)', fontSize: 11 }}>{t('comingSoon')}</div>
      </div>
    </div>
  );
}
